import { isKnown } from './carSchema';

/**
 * Joining the catalogue: the facts, the prose, and the guard between them and
 * the screen (SPEC-002).
 *
 * A car record is language-independent (`catalogue.json`) and its words live in
 * a per-language file (`prose.<lang>.json`). Neither half renders on its own —
 * the facts have no name and the prose has no figures — so the join is where a
 * car becomes something the garage can draw, and it is also the only place the
 * two halves are ever checked against each other.
 *
 * Both files are FETCHED, which is what makes the validation below load-bearing
 * rather than defensive: they are deployed independently of the bundle that
 * reads them, so a content edit can reach a browser running last week's code.
 */

/**
 * The closed vocabularies a record has to answer to, where each one lives, and
 * whether a card can be drawn at all without it.
 *
 * The first five are REQUIRED, and not because the schema likes being strict:
 * every one of them is DRAWN. `rarity` is the ribbon across the artwork and
 * reaches the screen as `cars.rarity.${value}`, `livery` is the plate's tint and
 * reaches the stylesheet as `--livery-${value}`, and the other three are the
 * axes the garage filters on. A record missing one does not fail — it prints
 * `cars.rarity.undefined` over the picture, or loses its tint to a custom
 * property nothing defines, or quietly matches no chip in the row. That silent
 * half-drawn card is precisely what this guard exists to keep off the screen,
 * and a value that is absent is no more drawable than a value that is invented.
 *
 * The rest are genuinely optional: they are rows on the spec sheet, and the
 * sheet already draws an em dash for a figure the catalogue does not carry. They
 * are listed all the same, because optional is not the same as unchecked — an
 * ABSENT valvetrain is a rotary and draws a dash, and an INVENTED one prints
 * `cars.valvetrain.dohc-99v` at a reader. `isStated` is what tells the two
 * apart, and it is why every one of them is `isRequired: false` rather than
 * missing from the list.
 *
 * This list has to stay level with the schema's own `VOCABULARIES`, and
 * `carCatalogue.test.js` holds it there. The build validates the committed pack,
 * but the app FETCHES its catalogue: a stale or hand-edited document can reach
 * this function having never met `validateCar.js`, and this is the last thing
 * between it and the screen.
 */
const VOCABULARY_FIELDS = [
    { field: 'decade', isRequired: true, valueOf: (car) => car.decade },
    { field: 'class', isRequired: true, valueOf: (car) => car.class },
    { field: 'origin', isRequired: true, valueOf: (car) => car.origin },
    { field: 'rarity', isRequired: true, valueOf: (car) => car.rarity },
    { field: 'livery', isRequired: true, valueOf: (car) => car.livery },
    { field: 'drivetrain', isRequired: false, valueOf: (car) => car.specs?.drivetrain },
    { field: 'aspiration', isRequired: false, valueOf: (car) => car.specs?.engine?.aspiration },
    { field: 'enginePosition', isRequired: false, valueOf: (car) => car.specs?.engine?.position },
    { field: 'engineConfiguration', isRequired: false, valueOf: (car) => car.specs?.engine?.configuration },
    { field: 'valvetrain', isRequired: false, valueOf: (car) => car.specs?.engine?.valvetrain },
    { field: 'transmissionType', isRequired: false, valueOf: (car) => car.specs?.transmission?.type },
];

/** The fields above, for the test that holds this list level with the schema. */
export const CHECKED_VOCABULARIES = VOCABULARY_FIELDS.map(({ field }) => field);

/** A field the pack did not state. `null` is authored absence; both are absent. */
function isStated(value) {
    return value !== undefined && value !== null;
}

/**
 * Why this record cannot be drawn — or `null` if it can.
 *
 * A sentence rather than a boolean, because the caller's whole job on a failure
 * is to say what went wrong loudly enough that somebody fixes the content pack.
 */
export function carRecordProblem(car, prose) {
    if (!car || typeof car.id !== 'string' || !car.id.trim()) {
        return 'the record carries no id';
    }

    if (!Number.isFinite(car.year)) {
        return 'the record carries no year, and the garage is ordered by year';
    }

    // Every card is four figures over a picture, and every one of them comes out
    // of `specs`. A record without it renders as a name over four em dashes.
    if (!car.specs || typeof car.specs !== 'object') {
        return 'the record carries no specs';
    }

    const missing = VOCABULARY_FIELDS
        .filter(({ isRequired, valueOf }) => isRequired && !isStated(valueOf(car)))
        .map(({ field }) => field);

    if (missing.length > 0) {
        return `the record carries no ${missing.join(', ')}`;
    }

    const unknown = VOCABULARY_FIELDS
        .filter(({ field, valueOf }) => isStated(valueOf(car)) && !isKnown(field, valueOf(car)))
        .map(({ field, valueOf }) => `${field}="${valueOf(car)}"`);

    if (unknown.length > 0) {
        return `the schema does not know ${unknown.join(', ')}`;
    }

    // Prose is not decoration here: the name, the tagline and the description
    // are the entire card apart from the picture and the four figures.
    return prose ? null : 'no pack carries its prose';
}

/**
 * Join a car to its prose, per record rather than per field.
 *
 * Prose for a language nobody has translated yet falls back to English, so a
 * missing `prose.de.json` renders an English description rather than an empty
 * card. A translated entry stands in for the English one WHOLE — a card that
 * took its name from one pack and its tagline from another would read as two
 * cars at once.
 */
export function joinCarProse(car, prose, fallbackProse = {}) {
    return { ...car, prose: prose?.[car.id] || fallbackProse?.[car.id] || null };
}

/**
 * The catalogue as the UI renders it: valid records only.
 *
 * A record that fails validation is DROPPED with a warning rather than thrown,
 * and that asymmetry is the whole design. Twelve cars are twelve independent
 * cards; one that cannot be drawn costs one card. Throwing would cost the
 * garage — a blank screen for a typo in a fun fact.
 *
 * `warn` arrives as a defaulted parameter so a suite can assert that the drop
 * was reported, which is the only thing that makes a silent drop safe.
 */
export function joinCatalogue(cars, prose, fallbackProse = {}, { warn = console.warn } = {}) {
    if (!Array.isArray(cars)) {
        return [];
    }

    return cars.reduce((catalogue, car) => {
        const joined = joinCarProse(car, prose, fallbackProse);
        const problem = carRecordProblem(car, joined.prose);

        if (problem) {
            warn(`Dropping the car ${car?.id ?? '(unnamed)'} from the garage: ${problem}.`);

            return catalogue;
        }

        catalogue.push(joined);

        return catalogue;
    }, []);
}

/**
 * The name the reader sees, which lives in the prose and nowhere else.
 *
 * The marque and model underneath it are a fallback rather than a second source:
 * `joinCatalogue` has already dropped anything without prose, so this branch is
 * only ever reached by a caller holding a raw record — the share sheet composing
 * a message before the join, say.
 */
export function carName(car) {
    const name = String(car?.prose?.name ?? '').trim();

    if (name) {
        return name;
    }

    return `${car?.make ?? ''} ${car?.model ?? ''}`.trim() || String(car?.id ?? '');
}

export function carTagline(car) {
    return String(car?.prose?.tagline ?? '').trim() || null;
}

/**
 * The name split the way a period stats screen printed it: the MARQUE on its own
 * line in small tracked capitals, the model under it in the display face
 * (SPEC-004 PR-4.8).
 *
 * It is a SPLIT of `carName` rather than a second reading of `make` and `model`,
 * and that is the whole point. The name a reader sees lives in the prose and
 * nowhere else, and the record's own `model` field is a shorter, tidier string
 * than the prose carries — "911" against "911 (993)", "Countach" against
 * "Countach 25th Anniversary". Printing the record's model would quietly show a
 * different car's name on the one screen a shared link opens on.
 *
 * So the marque is taken off the FRONT of the name, and only when it is actually
 * there. A pack whose prose names a car without its marque — or names it with a
 * different one, which is how a rebadged car would arrive — gets the whole name
 * as the model and no marque line at all, which is a lockup of one line rather
 * than a lockup with a wrong word in it.
 */
export function carNameParts(car) {
    const name = carName(car);
    const marque = String(car?.make ?? '').trim();

    if (!marque || !name.startsWith(`${marque} `)) {
        return { marque: null, model: name };
    }

    return { marque, model: name.slice(marque.length + 1) };
}

/**
 * The garage's order: by year, then by name.
 *
 * Year first because the product is about an ERA — the shelf reads as a walk
 * from 1986 to 1994, which is the story the catalogue is telling. Name second
 * because five of the twelve years carry two cars, and without a tiebreak their
 * order would be whatever the content file happened to list. That is invisible
 * until a swipe deals a card the shelf did not promise, at which point it is
 * unexplainable.
 */
export function sortCars(cars, languageCode = 'en') {
    return [...(cars || [])].sort((left, right) => (
        left.year - right.year || carName(left).localeCompare(carName(right), languageCode)
    ));
}

export function findCar(cars, carId) {
    return (cars || []).find((car) => car.id === carId) || null;
}
